import { showApiError, showToast } from '../main';
import {
  TOAST_SENT, TOAST_COMMENT_REJECTED,
  API_ERR_LOAD_ANSWERS, API_ERR_SAVE_ANSWER, API_ERR_SAVE_COMMENT,
  API_ERR_DELETE_ANSWER, API_ERR_SEND_REPLY,
} from '../utils/app-strings';
import { qaApi, chatsApi } from '../api';
import { clearDebounce, showSaveIndicator, loadingSpinner } from '../utils';
import { saveIndicator } from '../utils/html';
import { renderListItem } from '../components/list-item';
import {
  QaQuestionItem, AnswerEntry,
  escapeBasic, escapeAttr, renderControl, attachQuestionHandlers,
  collectAnswerData, renderAnswerReadonly, renderSaveButton,
  renderCommentInput, collectCommentText,
} from './qa-shared-renderers';
import { updateChatBreadcrumb } from './chat-breadcrumb';
import { on, off, joinQaSet, leaveQaSet } from '../socket';
import { SortRow, renderSortMenu, getSortLabel, setupSortMenuHandlers } from '../components/sort-menu';

// ============================================================================
// State
// ============================================================================

let loaded = false;
let currentOtherUserId = '';
let currentOtherUserName = '';
let currentSetId: string | null = null;
let currentChatId: string = '';
let answerMap = new Map<string, AnswerEntry>();
let pairwiseSocketHandler: ((data: any) => void) | null = null;
let answerSubmittedHandler: ((data: any) => void) | null = null;
let comparingHandler: ((data: any) => void) | null = null;
let comparedHandler: ((data: any) => void) | null = null;

// Sort state
const DEFAULT_SET_LIST_SORT = 'their-diff-desc';
const DEFAULT_PAIRWISE_SORT = 'num-asc';
let setListSortKey = DEFAULT_SET_LIST_SORT;
let pairwiseSortKey = DEFAULT_PAIRWISE_SORT;
let currentSets: any[] = [];
let currentPairwiseData: { set: any; questions: any[] } | null = null;

const SET_LIST_SORT_ROWS: SortRow[][] = [
  [
    { key: 'name-asc', icon: 'sort-alpha-down', label: 'Name A-Z' },
    { key: 'name-desc', icon: 'sort-alpha-up', label: 'Name Z-A' },
  ],
  [
    { key: 'match-desc', icon: 'trophy', label: 'Best Match' },
    { key: 'match-asc', icon: 'trophy', label: 'Worst Match' },
  ],
  [
    { key: 'my-diff-desc', icon: 'arrow-up', label: 'I Answered More' },
    { key: 'their-diff-desc', icon: 'arrow-down', label: 'They Answered More' },
  ],
];

const PAIRWISE_SORT_ROWS: SortRow[][] = [
  [
    { key: 'num-asc', icon: 'sort-numeric-down', label: '# Low → High' },
    { key: 'num-desc', icon: 'sort-numeric-up', label: '# High → Low' },
  ],
  [
    { key: 'match-desc', icon: 'trophy', label: 'Best Match' },
    { key: 'match-asc', icon: 'trophy', label: 'Worst Match' },
  ],
  [
    { key: 'my-unanswered', icon: 'question-circle', label: "I Didn't Answer" },
    { key: 'their-unanswered', icon: 'question-circle', label: "They Didn't Answer" },
  ],
];

// ============================================================================
// Public API
// ============================================================================

/** Set the chatId so this module can update URLs via history.replaceState */
export function setChatId(id: string): void {
  currentChatId = id;
}

export async function loadAnswersTab(
  otherUserId: string,
  otherUserName: string,
  initialSetId?: string,
  initialQuestionId?: string,
): Promise<void> {
  currentOtherUserId = otherUserId;
  currentOtherUserName = otherUserName;

  // If an initialSetId is given, jump straight to that set
  if (initialSetId) {
    loaded = true;
    currentSetId = initialSetId;
    answerMap = new Map();

    const container = document.getElementById('answers-content');
    if (!container) return;

    container.innerHTML = loadingSpinner();

    try {
      const res = await qaApi.getPairwiseAnswers(initialSetId, otherUserId);
      const setTitle = res.data.set?.title || 'Questions';
      renderPairwiseInline(container, res.data);
      updateChatBreadcrumb(setTitle);
      if (res.data.ai_answering) {
        showToast(`${res.data.ai_answering.name} is answering these now.`);
      }
      if (initialQuestionId) {
        requestAnimationFrame(() => {
          document.getElementById(`q-card-${initialQuestionId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    } catch (err: any) {
      container.innerHTML = `<div class="empty-state"><p>Failed to load questions</p></div>`;
      showApiError(err, API_ERR_LOAD_ANSWERS);
    }
    return;
  }

  if (loaded && !currentSetId) {
    // Already loaded - re-render from cache to restore sort header after tab switch
    const container = document.getElementById('answers-content');
    if (container) renderSetList(container, currentSets);
    return;
  }

  loaded = true;
  currentSetId = null;
  answerMap = new Map();

  const container = document.getElementById('answers-content');
  if (!container) return;

  container.innerHTML = loadingSpinner();

  try {
    const res = await qaApi.getPairwiseSets(otherUserId);
    const sets = res.data;
    renderSetList(container, sets);
    updateChatBreadcrumb();
  } catch (err: any) {
    container.innerHTML = `<div class="empty-state"><p>Failed to load question sets</p></div>`;
    showApiError(err, API_ERR_LOAD_ANSWERS);
  }
}

export function cleanupAnswersTab(): void {
  if (currentSetId) {
    leaveQaSet(currentSetId);
  }
  if (pairwiseSocketHandler) {
    off('answer:moderated', pairwiseSocketHandler);
    pairwiseSocketHandler = null;
  }
  if (answerSubmittedHandler) {
    off('qa:answer_submitted', answerSubmittedHandler);
    answerSubmittedHandler = null;
  }
  if (comparingHandler) {
    off('answer:comparing', comparingHandler);
    comparingHandler = null;
  }
  if (comparedHandler) {
    off('answer:compared', comparedHandler);
    comparedHandler = null;
  }
  hideSortHeader();
  loaded = false;
  currentSetId = null;
  currentChatId = '';
  currentOtherUserId = '';
  currentOtherUserName = '';
  answerMap = new Map();
  currentSets = [];
  currentPairwiseData = null;
  setListSortKey = DEFAULT_SET_LIST_SORT;
  pairwiseSortKey = DEFAULT_PAIRWISE_SORT;
}

// ============================================================================
// Sort Header Helper
// ============================================================================

function renderSortHeader(rows: SortRow[][], activeKey: string, label: string): void {
  const sortHeader = document.getElementById('answers-sort-header');
  const menuSlot = document.getElementById('answers-sort-menu-slot');
  if (sortHeader) {
    sortHeader.style.display = 'flex';
    sortHeader.innerHTML = `
      <button class="browse-pill" id="sort-btn">
        <span class="browse-pill-main">Sort <i class="fas fa-sort"></i></span>
        <span class="browse-pill-label" id="sort-label">${label}</span>
      </button>
    `;
  }
  if (menuSlot) {
    menuSlot.innerHTML = renderSortMenu(rows, activeKey);
  }
}

function hideSortHeader(): void {
  const sortHeader = document.getElementById('answers-sort-header');
  const menuSlot = document.getElementById('answers-sort-menu-slot');
  if (sortHeader) {
    sortHeader.style.display = 'none';
    sortHeader.innerHTML = '';
  }
  if (menuSlot) {
    menuSlot.innerHTML = '';
  }
}

// ============================================================================
// Set List View (Level 1)
// ============================================================================

function renderSetList(container: HTMLElement, sets: any[]): void {
  currentSets = sets;

  if (sets.length === 0) {
    hideSortHeader();
    container.innerHTML = `
      <div class="empty-state">
        <i class="fas fa-clipboard-list"></i>
        <h3>No question sets yet</h3>
        <p>Answer questions to compare with ${escapeBasic(currentOtherUserName)}</p>
      </div>
    `;
    return;
  }

  // Sort sets
  const sorted = [...sets];
  sortSets(sorted, setListSortKey);

  const sortLabel = getSortLabel(SET_LIST_SORT_ROWS, setListSortKey, 'Name A-Z');

  // Render sort button in the fixed header area
  renderSortHeader(SET_LIST_SORT_ROWS, setListSortKey, sortLabel);

  container.innerHTML = `
    <div id="pw-set-items">
      ${sorted.map(s => renderSetItem(s)).join('')}
    </div>
  `;

  // Attach click handlers
  for (const s of sets) {
    document.getElementById(`pw-set-${s.id}`)?.addEventListener('click', () => {
      if (currentChatId) {
        history.replaceState(null, '', `#/chat/${currentChatId}/answers/${s.id}`);
      }
      openPairwiseSet(s.id, s.title);
    });
  }

  // Attach sort handlers
  setupSortMenuHandlers(
    () => setListSortKey,
    (key) => {
      setListSortKey = key;
      renderSetList(container, currentSets);
      const scroll = document.getElementById('answers-content');
      if (scroll) scroll.scrollTop = 0;
    },
  );
}

function renderSetItem(s: any): string {
  const you = s.my_answered_count || 0;
  const them = s.their_answered_count || 0;
  const bothAnswered = s.both_answered_count || 0;
  const total = s.question_count || 0;
  const score = parseFloat(s.match_score) || 0;
  const diff = you - them;
  const diffStr = diff > 0 ? `+${diff}` : `${diff}`;

  let preview = `You: ${you}/${total} (${diffStr}) · ${escapeBasic(currentOtherUserName)}: ${them}/${total}`;
  if (bothAnswered > 0) {
    const pct = Math.round((score / bothAnswered) * 100);
    preview += ` · ${pct}% match`;
  }

  const metaParts: string[] = [];
  if (s.category_short_title || s.category_title) metaParts.push(escapeBasic(s.category_short_title || s.category_title!));
  if (s.topic_title) metaParts.push(escapeBasic(s.topic_title));
  const metaHtml = metaParts.length > 0
    ? `<span class="badge badge-muted pw-set-badge">${metaParts.join(' · ')}</span>`
    : '';

  return renderListItem({
    id: `pw-set-${s.id}`,
    avatarUrl: s.avatar_url,
    avatarFallbackIcon: 'clipboard-list',
    title: s.title,
    preview,
    metaHtml,
    cssClass: 'pw-set-item',
  });
}

function categoryThenTitle(a: any, b: any): number {
  const cat = (a.category_title || '').localeCompare(b.category_title || '');
  return cat !== 0 ? cat : (a.title || '').localeCompare(b.title || '');
}

function sortSets(sets: any[], key: string): void {
  sets.sort((a, b) => {
    switch (key) {
      case 'name-asc': {
        const cat = (a.category_title || '').localeCompare(b.category_title || '');
        return cat !== 0 ? cat : (a.title || '').localeCompare(b.title || '');
      }
      case 'name-desc': {
        const cat = (b.category_title || '').localeCompare(a.category_title || '');
        return cat !== 0 ? cat : (b.title || '').localeCompare(a.title || '');
      }
      case 'match-desc': {
        const pctA = (a.both_answered_count || 0) > 0 ? (parseFloat(a.match_score) || 0) / a.both_answered_count : 0;
        const pctB = (b.both_answered_count || 0) > 0 ? (parseFloat(b.match_score) || 0) / b.both_answered_count : 0;
        return (pctB - pctA) || categoryThenTitle(a, b);
      }
      case 'match-asc': {
        const pctA2 = (a.both_answered_count || 0) > 0 ? (parseFloat(a.match_score) || 0) / a.both_answered_count : 0;
        const pctB2 = (b.both_answered_count || 0) > 0 ? (parseFloat(b.match_score) || 0) / b.both_answered_count : 0;
        return (pctA2 - pctB2) || categoryThenTitle(a, b);
      }
      case 'my-diff-desc': {
        const diff = ((b.my_answered_count || 0) - (b.their_answered_count || 0)) - ((a.my_answered_count || 0) - (a.their_answered_count || 0));
        return diff || categoryThenTitle(a, b);
      }
      case 'their-diff-desc': {
        const diff = ((b.their_answered_count || 0) - (b.my_answered_count || 0)) - ((a.their_answered_count || 0) - (a.my_answered_count || 0));
        return diff || categoryThenTitle(a, b);
      }
      default:
        return 0;
    }
  });
}

function sortPairwiseQuestions(questions: PairwiseQuestion[]): void {
  questions.sort((a, b) => {
    switch (pairwiseSortKey) {
      case 'num-asc':
        return a.order_index - b.order_index;
      case 'num-desc':
        return b.order_index - a.order_index;
      case 'match-desc': {
        const scoreA = (a.my_answer && a.their_answer) ? matchScore(a) : -1;
        const scoreB = (b.my_answer && b.their_answer) ? matchScore(b) : -1;
        return (scoreB - scoreA) || (a.order_index - b.order_index);
      }
      case 'match-asc': {
        const scoreA2 = (a.my_answer && a.their_answer) ? matchScore(a) : 2;
        const scoreB2 = (b.my_answer && b.their_answer) ? matchScore(b) : 2;
        return (scoreA2 - scoreB2) || (a.order_index - b.order_index);
      }
      case 'my-unanswered': {
        // I didn't answer first, then answered; secondary sort by #
        const uA = a.my_answer ? 1 : 0;
        const uB = b.my_answer ? 1 : 0;
        return uA !== uB ? uA - uB : a.order_index - b.order_index;
      }
      case 'their-unanswered': {
        // They didn't answer first, then answered; secondary sort by #
        const uA2 = a.they_answered ? 1 : 0;
        const uB2 = b.they_answered ? 1 : 0;
        return uA2 !== uB2 ? uA2 - uB2 : a.order_index - b.order_index;
      }
      default:
        return 0;
    }
  });
}

// ============================================================================
// Pairwise Set View (Level 2 - inline in tab)
// ============================================================================

async function openPairwiseSet(setId: string, setTitle: string): Promise<void> {
  currentSetId = setId;
  answerMap = new Map();

  const container = document.getElementById('answers-content');
  if (!container) return;

  container.innerHTML = loadingSpinner();

  try {
    const res = await qaApi.getPairwiseAnswers(setId, currentOtherUserId);
    renderPairwiseInline(container, res.data);
    updateChatBreadcrumb(setTitle);
    if (res.data.ai_answering) {
      showToast(`${res.data.ai_answering.name} is answering these now.`);
    }
  } catch (err: any) {
    container.innerHTML = `<div class="empty-state"><p>Failed to load questions</p></div>`;
    showApiError(err, API_ERR_LOAD_ANSWERS);
  }
}

/** Render the pairwise questions into a container (title now in breadcrumb) */
function renderPairwiseInline(container: HTMLElement, data: { set: any; questions: any[] }): void {
  container.innerHTML = `
    <div id="pw-questions-content">
      ${loadingSpinner()}
    </div>
  `;

  renderPairwiseQuestions(data);
}

function renderPairwiseQuestions(data: { set: any; questions: any[] }): void {
  const qContent = document.getElementById('pw-questions-content');
  if (!qContent) return;

  // Join QA set room for real-time updates
  if (currentSetId) {
    joinQaSet(currentSetId);
  }

  // Register socket listeners for live updates (direct DOM updates, no GET requests)

  // Handler for answer:moderated - update mod badge directly from payload
  if (!pairwiseSocketHandler) {
    pairwiseSocketHandler = (evt: any) => {
      if (evt.setId !== currentSetId) return;
      const { questionId, status, scores } = evt;

      // Update in-memory data
      if (currentPairwiseData) {
        const q = currentPairwiseData.questions.find((pq: any) => pq.id === questionId);
        if (q?.my_answer) {
          q.my_answer.mod_score = status === 'low_quality' ? 'low_quality'
            : (scores?.relevance != null ? Math.round(scores.relevance * 100) : null);
        }
      }

      // Update DOM: find my answer column and update mod badge
      const card = document.querySelector(`[data-question-id="${questionId}"]`);
      if (!card) return;
      const myCol = card.querySelector('.pw-my-answer');
      if (!myCol) return;

      const labelEl = myCol.querySelector('.pw-answer-label');
      if (labelEl) {
        // Remove old badge, add new one
        const existingBadge = labelEl.querySelector('.pw-mod-badge');
        if (existingBadge) existingBadge.remove();

        if (status === 'low_quality') {
          labelEl.insertAdjacentHTML('beforeend',
            `<span class="pw-mod-badge pw-mod-low-quality"><i class="fas fa-exclamation-triangle"></i> Low Quality (try again)</span>`);
        } else if (scores?.relevance != null) {
          const pct = Math.round(scores.relevance * 100);
          labelEl.insertAdjacentHTML('beforeend',
            `<span class="pw-mod-badge"><i class="fas fa-star"></i> ${pct}%</span>`);
        }
      }

      // If approved and they had answered, we may need to reveal their answer
      // This requires server data (we don't have their answer text), so do a targeted refetch
      if (status !== 'low_quality') {
        const q = currentPairwiseData?.questions.find((pq: any) => pq.id === questionId);
        if (q?.they_answered && !q?.their_answer) {
          // They answered but we haven't revealed yet - reciprocity just met
          refetchAndUpdateCards([questionId]);
        }
      }
    };
    on('answer:moderated', pairwiseSocketHandler);
  }

  // Handler for qa:answer_submitted - update DOM directly (no GET request)
  // Note: emitToQaSet excludes the sender, so we only receive others' answers
  if (!answerSubmittedHandler) {
    answerSubmittedHandler = (evt: any) => {
      if (evt.setId !== currentSetId) return;
      const { questionId } = evt;

      // Update in-memory data
      if (currentPairwiseData) {
        const q = currentPairwiseData.questions.find((pq: any) => pq.id === questionId);
        if (q && !q.they_answered) {
          q.they_answered = true;
        }
      }

      // Update DOM: change "their" column from "Not yet answered" to locked
      const card = document.querySelector(`[data-question-id="${questionId}"]`);
      if (!card) return;
      const theirCol = card.querySelector('.pw-answer-col:first-child');
      if (theirCol && (theirCol.classList.contains('pw-answer-empty') || theirCol.querySelector('.text-muted'))) {
        theirCol.classList.remove('pw-answer-empty');
        theirCol.innerHTML = `
          <div class="pw-answer-label">${escapeBasic(currentOtherUserName)}</div>
          <div class="pw-lock-badge"><i class="fas fa-lock"></i> Answer to reveal</div>`;
      }

      recalcProgress();
    };
    on('qa:answer_submitted', answerSubmittedHandler);
  }

  // Socket listener: AI comparison started - show "Reading..." indicator
  if (!comparingHandler) {
    comparingHandler = (evt: any) => {
      if (evt.setId !== currentSetId) return;
      const card = document.querySelector(`[data-question-id="${evt.questionId}"]`);
      if (!card) return;
      const stacked = card.querySelector('.pw-stacked');
      if (!stacked) return;

      const loadingHTML = `
        <div class="pw-answer-header"><div class="pw-answer-label">AI</div></div>
        <div class="pw-answer-value"><span class="typing-dots">Comparing</span></div>`;

      const existingAiBox = stacked.querySelector('.pw-ai-box');
      if (existingAiBox) {
        // Replace old comparison with loading state
        existingAiBox.classList.add('pw-ai-loading');
        existingAiBox.innerHTML = loadingHTML;
      } else {
        const aiLoading = document.createElement('div');
        aiLoading.className = 'pw-answer-col pw-ai-box pw-ai-loading';
        aiLoading.innerHTML = loadingHTML;
        stacked.appendChild(aiLoading);
      }
    };
    on('answer:comparing', comparingHandler);
  }

  // Socket listener: AI comparison done - update DOM directly from payload (no GET)
  if (!comparedHandler) {
    comparedHandler = (evt: any) => {
      if (evt.setId !== currentSetId || evt.otherUserId !== currentOtherUserId) return;
      const { questionId, score, comment } = evt;

      // Update in-memory data
      if (currentPairwiseData) {
        const q = currentPairwiseData.questions.find((pq: any) => pq.id === questionId);
        if (q) q.ai_comparison = { score, comment };
      }

      // Update DOM: find card and update/create AI box
      const card = document.querySelector(`[data-question-id="${questionId}"]`);
      if (!card) return;
      const stacked = card.querySelector('.pw-stacked');
      if (!stacked) return;

      const aiHTML = `
        <div class="pw-answer-header">
          <div class="pw-answer-label">AI</div>
        </div>
        <div class="pw-answer-value">${escapeBasic(comment || '')}</div>`;

      const existingAiBox = stacked.querySelector('.pw-ai-box');
      if (existingAiBox) {
        existingAiBox.classList.remove('pw-ai-loading');
        existingAiBox.innerHTML = aiHTML;
      } else {
        const aiBox = document.createElement('div');
        aiBox.className = 'pw-answer-col pw-ai-box';
        aiBox.innerHTML = aiHTML;
        stacked.appendChild(aiBox);
      }

      // Update score badge in card header
      const header = card.querySelector('.qs-card-header');
      if (header) {
        const existingBadge = header.querySelector('.pw-score-badge');
        if (existingBadge) existingBadge.remove();
        const tier = Math.min(80, Math.floor(score / 20) * 20);
        header.insertAdjacentHTML('beforeend',
          `<span class="pw-score-badge pw-score-${tier}">${score}%</span>`);
      }

      recalcProgress();
    };
    on('answer:compared', comparedHandler);
  }

  // Stash data for sort re-renders
  currentPairwiseData = data;

  const questions = [...data.questions] as PairwiseQuestion[];
  const total = questions.length;
  const answeredCount = questions.filter(q => q.my_answer).length;
  const theirAnsweredCount = questions.filter(q => q.they_answered || q.their_answer).length;
  const scoredQuestions = questions.filter(q => q.my_answer && q.their_answer && q.ai_comparison);
  const totalMatchScore = scoredQuestions.reduce((sum, q) => sum + matchScore(q), 0);

  const diff = answeredCount - theirAnsweredCount;
  const diffStr = diff > 0 ? `+${diff}` : `${diff}`;
  let statsText = `You: ${answeredCount}/${total} (${diffStr}) · ${escapeBasic(currentOtherUserName)}: ${theirAnsweredCount}/${total}`;
  if (scoredQuestions.length > 0) {
    const pct = Math.round((totalMatchScore / scoredQuestions.length) * 100);
    statsText += ` · ${pct}% match`;
  }

  // Sort questions
  sortPairwiseQuestions(questions);

  // Build answer map for auto-save
  answerMap = new Map();
  for (const q of questions) {
    if (q.my_answer) {
      answerMap.set(q.id, {
        answerId: q.my_answer.answer_id,
        version: {
          answer_text: q.my_answer.answer_text,
          answer_number: q.my_answer.answer_number,
          answer_date: q.my_answer.answer_date,
          selected_choice_ids: q.my_answer.selected_choice_ids,
          other_text: q.my_answer.other_text,
        },
        comment_text: q.my_answer.comment_text,
      });
    }
  }

  const pwSortLabel = getSortLabel(PAIRWISE_SORT_ROWS, pairwiseSortKey, '# Low → High');

  // Render sort button in the fixed header area
  renderSortHeader(PAIRWISE_SORT_ROWS, pairwiseSortKey, pwSortLabel);

  const labelParts: string[] = [];
  if (data.set.category_short_title || data.set.category_title) labelParts.push(escapeBasic(data.set.category_short_title || data.set.category_title!));
  if (data.set.topic_title) labelParts.push(escapeBasic(data.set.topic_title));
  const categoryLine = labelParts.length > 0
    ? `<div class="pw-category-label">${labelParts.join(' · ')}</div>`
    : '';

  qContent.innerHTML = `
    <div class="qs-progress" id="pw-progress">
      ${statsText}
    </div>
    ${categoryLine}
    <div class="qs-questions">
      ${questions.map(q => renderPairwiseCard(q)).join('')}
    </div>
  `;

  // Attach sort handlers
  setupSortMenuHandlers(
    () => pairwiseSortKey,
    (key) => {
      pairwiseSortKey = key;
      if (currentPairwiseData) renderPairwiseQuestions(currentPairwiseData);
      const scroll = document.getElementById('answers-content');
      if (scroll) scroll.scrollTop = 0;
    },
  );

  // Attach event handlers
  for (let i = 0; i < questions.length; i++) {
    const pw = questions[i] as PairwiseQuestion;
    const hasMyAnswer = !!pw.my_answer;

    if (hasMyAnswer) {
      // States 1 (revealed) and 3a (only I answered): read-only with Edit button
      attachEditButton(pw);
    } else {
      // States 2 (locked) and 3b (neither answered): all types use Save button (no auto-save)
      attachQuestionHandlers(pw as QaQuestionItem, () => {}, {
        skipFreeTextAutoSave: true,
        skipMultiChoiceAutoSave: true,
        skipNumericAutoSave: true,
        skipSingleChoiceAutoSave: true,
        skipDateAutoSave: true,
        skipScaleAutoSave: true,
      });
      attachFreeTextSaveButton(pw);
    }

    if (isBothRevealed(pw)) {
      attachReplyButton(pw);
    }
  }
}

// ============================================================================
// Pairwise Question Card States
// ============================================================================

interface PairwiseQuestion extends QaQuestionItem {
  my_answer: {
    answer_id: string;
    answer_text: string | null;
    answer_number: number | null;
    answer_date: string | null;
    selected_choice_ids: string[] | null;
    other_text: string | null;
    comment_text: string | null;
    mod_score: number | 'pending' | 'low_quality' | null;
  } | null;
  they_answered: boolean;
  their_answer: {
    answer_text: string | null;
    answer_number: number | null;
    answer_date: string | null;
    selected_choice_ids: string[] | null;
    other_text: string | null;
    comment_text: string | null;
    mod_score: number | 'pending' | 'low_quality' | null;
  } | null;
  ai_comparison: {
    score: number;
    comment: string;
  } | null;
}

function renderModBadge(modScore: number | 'pending' | 'low_quality' | null | undefined): string {
  if (modScore == null) return '';
  if (modScore === 'pending') {
    return `<span class="pw-mod-badge pw-mod-pending"><i class="fas fa-star"></i> Pending</span>`;
  }
  if (modScore === 'low_quality') {
    return `<span class="pw-mod-badge pw-mod-low-quality"><i class="fas fa-exclamation-triangle"></i> Low Quality (try again)</span>`;
  }
  return `<span class="pw-mod-badge"><i class="fas fa-star"></i> ${modScore}%</span>`;
}

function isBothRevealed(q: PairwiseQuestion): boolean {
  if (!q.my_answer || !q.their_answer) return false;
  if (q.my_answer.mod_score === 'low_quality' || q.my_answer.mod_score === 'pending') return false;
  return true;
}

function renderPairwiseCard(q: PairwiseQuestion): string {
  const hasMyAnswer = !!q.my_answer;
  const hasTheirAnswer = q.they_answered;
  const bothRevealed = isBothRevealed(q);

  // State 1: Both answered and revealed
  if (bothRevealed) {
    return renderRevealedCard(q);
  }

  // State 2: They answered, I haven't
  if (hasTheirAnswer && !hasMyAnswer) {
    return renderLockedCard(q);
  }

  // State 3: Neither answered, or only I answered
  return renderEditableCard(q, hasMyAnswer);
}

function renderRevealedCard(q: PairwiseQuestion): string {
  const myDisplay = renderAnswerReadonly(q, q.my_answer!);
  const myComment = q.my_answer?.comment_text;
  const theirDisplay = renderAnswerReadonly(q, q.their_answer!);
  const theirComment = q.their_answer?.comment_text;

  // Check if answers match for highlight (score >= 0.5 for free_text, exact for others)
  const matchClass = matchScore(q) >= 0.5 ? ' pw-answer-match' : '';

  // AI comparison box (only for revealed free_text with comparison data)
  const aiBox = q.ai_comparison ? `
        <div class="pw-answer-col pw-ai-box">
          <div class="pw-answer-header">
            <div class="pw-answer-label">AI</div>
          </div>
          <div class="pw-answer-value">${escapeBasic(q.ai_comparison.comment)}</div>
        </div>` : '';

  return `
    <div class="card card-bordered qs-card pw-card pw-card-revealed" id="q-card-${q.id}" data-question-id="${q.id}">
      <div class="qs-card-header">
        <span class="qs-card-num">${q.order_index}.</span>
        <span class="qs-card-text">${escapeBasic(q.question_text)}</span>
        ${saveIndicator(`indicator-q-${q.id}`)}
        ${renderScoreBadge(q)}
      </div>
      ${q.help_text ? `<div class="qs-card-help">${escapeBasic(q.help_text)}</div>` : ''}
      <div class="pw-stacked${matchClass}">
        <div class="pw-answer-col" id="pw-their-${q.id}">
          <div class="pw-answer-header">
            <div class="pw-answer-label">${escapeBasic(currentOtherUserName)} ${renderModBadge(q.their_answer?.mod_score)}</div>
            <button class="btn btn-outline btn-sm pw-reply-btn" id="pw-reply-btn-${q.id}">Reply</button>
          </div>
          <div class="pw-answer-value">${theirDisplay}</div>
          ${theirComment ? `<div class="pw-answer-comment">${escapeBasic(theirComment)}</div>` : ''}
          <div class="pw-reply-area" id="pw-reply-area-${q.id}" style="display:none"></div>
        </div>
        <div class="pw-answer-col pw-my-answer" id="pw-my-${q.id}">
          <div class="pw-answer-header">
            <div class="pw-answer-label">You ${renderModBadge(q.my_answer?.mod_score)}</div>
            <button class="btn btn-outline btn-sm pw-edit-btn" id="pw-edit-btn-${q.id}">Edit</button>
          </div>
          <div class="pw-answer-value">${myDisplay}</div>
          ${myComment ? `<div class="pw-answer-comment">${escapeBasic(myComment)}</div>` : ''}
          <div class="pw-edit-area" style="display:none">
          </div>
        </div>${aiBox}
      </div>
    </div>
  `;
}

function renderLockedCard(q: PairwiseQuestion): string {
  const entry = answerMap.get(q.id);
  const isNonFreeText = q.answer_type !== 'free_text';

  return `
    <div class="card card-bordered qs-card pw-card" id="q-card-${q.id}" data-question-id="${q.id}">
      <div class="qs-card-header">
        <span class="qs-card-num">${q.order_index}.</span>
        <span class="qs-card-text">${escapeBasic(q.question_text)}</span>
        ${saveIndicator(`indicator-q-${q.id}`)}
      </div>
      ${q.help_text ? `<div class="qs-card-help">${escapeBasic(q.help_text)}</div>` : ''}
      <div class="pw-stacked">
        <div class="pw-answer-col">
          <div class="pw-answer-label">${escapeBasic(currentOtherUserName)}</div>
          <div class="pw-lock-badge">
            <i class="fas fa-lock"></i> Answer to reveal
          </div>
        </div>
        <div class="pw-answer-col">
          <div class="pw-answer-label">You</div>
          <div class="qs-card-control" id="q-control-${q.id}">
            ${renderControl(q, entry)}
            ${isNonFreeText ? renderCommentInput(q.id, entry?.comment_text) : ''}
            ${renderSaveButton(q.id)}
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderEditableCard(q: PairwiseQuestion, hasMyAnswer: boolean): string {
  const entry = answerMap.get(q.id);

  if (hasMyAnswer) {
    // Sub-case 3a: I've answered, they haven't (or reciprocity not met yet) → read-only with Edit
    const myDisplay = renderAnswerReadonly(q, q.my_answer!);
    const myComment = q.my_answer?.comment_text;
    const theirSection = q.they_answered
      ? `<div class="pw-answer-label">${escapeBasic(currentOtherUserName)}</div>
         <div class="pw-lock-badge"><i class="fas fa-lock"></i> Answer to reveal</div>`
      : `<div class="pw-answer-label">${escapeBasic(currentOtherUserName)}</div>
         <div class="pw-answer-value text-muted">Not yet answered</div>`;
    return `
      <div class="card card-bordered qs-card pw-card" id="q-card-${q.id}" data-question-id="${q.id}">
        <div class="qs-card-header">
          <span class="qs-card-num">${q.order_index}.</span>
          <span class="qs-card-text">${escapeBasic(q.question_text)}</span>
          ${saveIndicator(`indicator-q-${q.id}`)}
        </div>
        ${q.help_text ? `<div class="qs-card-help">${escapeBasic(q.help_text)}</div>` : ''}
        <div class="pw-stacked">
          <div class="pw-answer-col${q.they_answered ? '' : ' pw-answer-empty'}">
            ${theirSection}
          </div>
          <div class="pw-answer-col pw-my-answer" id="pw-my-${q.id}">
            <div class="pw-answer-header">
              <div class="pw-answer-label">You ${renderModBadge(q.my_answer?.mod_score)}</div>
              <button class="btn btn-outline btn-sm pw-edit-btn" id="pw-edit-btn-${q.id}">Edit</button>
            </div>
            <div class="pw-answer-value">${myDisplay}</div>
            ${myComment ? `<div class="pw-answer-comment">${escapeBasic(myComment)}</div>` : ''}
            <div class="pw-edit-area" style="display:none">
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Sub-case 3b: Neither has answered → direct input controls
  const isNonFreeText = q.answer_type !== 'free_text';
  return `
    <div class="card card-bordered qs-card pw-card" id="q-card-${q.id}" data-question-id="${q.id}">
      <div class="qs-card-header">
        <span class="qs-card-num">${q.order_index}.</span>
        <span class="qs-card-text">${escapeBasic(q.question_text)}</span>
        ${saveIndicator(`indicator-q-${q.id}`)}
      </div>
      ${q.help_text ? `<div class="qs-card-help">${escapeBasic(q.help_text)}</div>` : ''}
      <div class="pw-stacked">
        <div class="pw-answer-col pw-answer-empty">
          <div class="pw-answer-label">${escapeBasic(currentOtherUserName)}</div>
          <div class="pw-answer-value text-muted">Not yet answered</div>
        </div>
        <div class="pw-answer-col">
          <div class="pw-answer-label">You</div>
          <div class="qs-card-control" id="q-control-${q.id}">
            ${renderControl(q, entry)}
            ${isNonFreeText ? renderCommentInput(q.id, entry?.comment_text) : ''}
            ${renderSaveButton(q.id)}
          </div>
        </div>
      </div>
    </div>
  `;
}

// ============================================================================
// Answer Matching
// ============================================================================

function matchScore(q: PairwiseQuestion): number {
  if (!q.my_answer || !q.their_answer) return 0;

  // Use AI comparison score when available (works for all answer types)
  if (q.ai_comparison?.score != null) return q.ai_comparison.score / 100;

  switch (q.answer_type) {
    case 'single_choice': {
      const myId = q.my_answer.selected_choice_ids?.[0];
      const theirId = q.their_answer.selected_choice_ids?.[0];
      if (myId && theirId) return myId === theirId ? 1 : 0;
      if (!myId && !theirId && q.my_answer.other_text && q.their_answer.other_text) {
        return q.my_answer.other_text.toLowerCase() === q.their_answer.other_text.toLowerCase() ? 1 : 0;
      }
      return 0;
    }
    case 'multi_choice': {
      const myIds = new Set(q.my_answer.selected_choice_ids || []);
      const theirIds = new Set(q.their_answer.selected_choice_ids || []);
      if (myIds.size !== theirIds.size) return 0;
      for (const id of myIds) { if (!theirIds.has(id)) return 0; }
      return 1;
    }
    case 'free_text':
      return (q.my_answer.answer_text || '').toLowerCase().trim() ===
             (q.their_answer.answer_text || '').toLowerCase().trim() ? 1 : 0;
    case 'numeric':
    case 'scale':
      return q.my_answer.answer_number === q.their_answer.answer_number ? 1 : 0;
    case 'date':
      return q.my_answer.answer_date === q.their_answer.answer_date ? 1 : 0;
    default:
      return 0;
  }
}

function renderScoreBadge(q: PairwiseQuestion): string {
  if (!q.ai_comparison) return '';
  const score = q.ai_comparison.score;
  const tier = Math.min(80, Math.floor(score / 20) * 20);
  return `<span class="pw-score-badge pw-score-${tier}">${score}%</span>`;
}

// ============================================================================
// Plain Answer Text (for reply context)
// ============================================================================

function getPlainAnswerText(q: PairwiseQuestion, answer: NonNullable<PairwiseQuestion['their_answer']>): string {
  switch (q.answer_type) {
    case 'single_choice':
    case 'multi_choice': {
      const selectedIds = new Set(answer.selected_choice_ids || []);
      const labels = q.choices
        .filter(c => selectedIds.has(c.id))
        .map(c => c.choice_text);
      if (answer.other_text) labels.push(answer.other_text);
      return labels.join(', ') || '';
    }
    case 'free_text':
      return answer.answer_text || '';
    case 'numeric':
    case 'scale': {
      if (answer.answer_number == null) return '';
      const unit = q.unit ? ` ${q.unit}` : '';
      return `${answer.answer_number}${unit}`;
    }
    case 'date':
      return answer.answer_date || '';
    default:
      return '';
  }
}

// ============================================================================
// Targeted Card Refresh (preserves in-progress edits)
// ============================================================================

/** Check if a card is being actively edited by the user */
function isCardActive(cardEl: Element, questionId: string): boolean {
  // 0. Reply input is open (user is composing a reply)
  if (cardEl.querySelector('.pw-reply-input')) return true;

  // 1. Edit button toggled on (user clicked Edit on a read-only card)
  if (cardEl.querySelector('.pw-my-answer.editing')) return true;

  // 2. Focus is inside this card (user has cursor in a textarea/input)
  const active = document.activeElement;
  if (active && cardEl.contains(active)) return true;

  // 3. Control area has unsaved content
  const control = cardEl.querySelector(`#q-control-${questionId}`);
  if (control) {
    // Free text textarea with content
    const textarea = control.querySelector('textarea') as HTMLTextAreaElement | null;
    if (textarea && textarea.value.trim()) {
      // Only "active" if there's no saved answer yet (i.e. new unsaved input)
      if (!answerMap.has(questionId)) return true;
    }
    // Choice inputs that are checked
    const checked = control.querySelectorAll('input:checked');
    if (checked.length > 0 && !answerMap.has(questionId)) return true;
  }

  return false;
}

/** Surgically update only non-interactive parts of an active card */
function updateActiveCard(cardEl: Element, q: PairwiseQuestion): void {
  const stacked = cardEl.querySelector('.pw-stacked');
  if (!stacked) return;

  // Update "their" column (always the first child of .pw-stacked)
  const theirCol = stacked.children[0];
  if (theirCol && !theirCol.querySelector('#q-control-' + q.id)) {
    // This is indeed the "their" column - rebuild its innerHTML
    let theirHTML: string;
    if (isBothRevealed(q)) {
      const theirDisplay = renderAnswerReadonly(q, q.their_answer!);
      const theirComment = q.their_answer?.comment_text;
      theirHTML = `
        <div class="pw-answer-label">${escapeBasic(currentOtherUserName)} ${renderModBadge(q.their_answer?.mod_score)}</div>
        <div class="pw-answer-value">${theirDisplay}</div>
        ${theirComment ? `<div class="pw-answer-comment">${escapeBasic(theirComment)}</div>` : ''}`;
    } else if (q.they_answered) {
      theirHTML = `
        <div class="pw-answer-label">${escapeBasic(currentOtherUserName)}</div>
        <div class="pw-lock-badge"><i class="fas fa-lock"></i> Answer to reveal</div>`;
    } else {
      theirHTML = `
        <div class="pw-answer-label">${escapeBasic(currentOtherUserName)}</div>
        <div class="pw-answer-value text-muted">Not yet answered</div>`;
    }
    theirCol.innerHTML = theirHTML;
  }

  // Update AI comparison box - add, replace, or leave alone
  const existingAiBox = stacked.querySelector('.pw-ai-box');
  if (q.ai_comparison) {
    const aiHTML = `
      <div class="pw-answer-header">
        <div class="pw-answer-label">AI</div>
      </div>
      <div class="pw-answer-value">${escapeBasic(q.ai_comparison.comment)}</div>`;
    if (existingAiBox) {
      existingAiBox.classList.remove('pw-ai-loading');
      existingAiBox.innerHTML = aiHTML;
    } else {
      const aiBox = document.createElement('div');
      aiBox.className = 'pw-answer-col pw-ai-box';
      aiBox.innerHTML = aiHTML;
      stacked.appendChild(aiBox);
    }
  }
}

/** Attach event handlers to a single pairwise card after DOM insertion */
function attachCardHandlers(q: PairwiseQuestion): void {
  const hasMyAnswer = !!q.my_answer;

  if (hasMyAnswer) {
    attachEditButton(q);
  } else {
    attachQuestionHandlers(q as QaQuestionItem, () => {}, {
      skipFreeTextAutoSave: true,
      skipMultiChoiceAutoSave: true,
      skipNumericAutoSave: true,
      skipSingleChoiceAutoSave: true,
      skipDateAutoSave: true,
      skipScaleAutoSave: true,
    });
    attachFreeTextSaveButton(q);
  }

  if (isBothRevealed(q)) {
    attachReplyButton(q);
  }
}

/**
 * Refresh pairwise cards without destroying in-progress edits.
 * Called instead of renderPairwiseQuestions() for all updates after initial render.
 */
function refreshPairwiseCards(
  data: { set: any; questions: any[] },
  forceUpdateIds?: Set<string>,
): void {
  const qContent = document.getElementById('pw-questions-content');
  if (!qContent) return;

  // Sort incoming questions the same way the DOM was rendered, so index-based
  // card matching stays aligned with the current display order.
  const questions = [...data.questions] as PairwiseQuestion[];
  sortPairwiseQuestions(questions);

  // Keep stashed data in sync so sort-change re-renders use fresh answers
  currentPairwiseData = { set: data.set, questions };

  const questionsContainer = qContent.querySelector('.qs-questions');

  // Fall back to full render if structure changed (question count mismatch or missing container)
  if (!questionsContainer || questionsContainer.children.length !== questions.length) {
    renderPairwiseQuestions(data);
    return;
  }

  // --- Update progress bar in-place ---
  const total = questions.length;
  const answeredCount = questions.filter(q => q.my_answer).length;
  const theirAnsweredCount = questions.filter(q => q.they_answered || q.their_answer).length;
  const bothAnsweredCount = questions.filter(q => q.my_answer && q.they_answered).length;
  const totalMatchScore = questions
    .filter(q => q.my_answer && q.their_answer)
    .reduce((sum, q) => sum + matchScore(q), 0);

  const rDiff = answeredCount - theirAnsweredCount;
  const rDiffStr = rDiff > 0 ? `+${rDiff}` : `${rDiff}`;
  let statsText = `You: ${answeredCount}/${total} (${rDiffStr}) · ${escapeBasic(currentOtherUserName)}: ${theirAnsweredCount}/${total}`;
  if (bothAnsweredCount > 0) {
    const pct = Math.round((totalMatchScore / bothAnsweredCount) * 100);
    statsText += ` · ${pct}% match`;
  }

  const progressEl = document.getElementById('pw-progress');
  if (progressEl) progressEl.innerHTML = statsText;

  // --- Per-card update ---
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    const cardEl = questionsContainer.children[i];
    if (!cardEl) continue;

    const forceThis = forceUpdateIds?.has(q.id) ?? false;

    if (forceThis || !isCardActive(cardEl, q.id)) {
      // Full card replacement
      const temp = document.createElement('div');
      temp.innerHTML = renderPairwiseCard(q);
      const newCard = temp.firstElementChild;
      if (!newCard) continue;

      cardEl.replaceWith(newCard);

      // Update answerMap for this question
      if (q.my_answer) {
        answerMap.set(q.id, {
          answerId: q.my_answer.answer_id,
          version: {
            answer_text: q.my_answer.answer_text,
            answer_number: q.my_answer.answer_number,
            answer_date: q.my_answer.answer_date,
            selected_choice_ids: q.my_answer.selected_choice_ids,
            other_text: q.my_answer.other_text,
          },
          comment_text: q.my_answer.comment_text,
        });
      }

      attachCardHandlers(q);
    } else {
      // Surgical update - only touch non-interactive parts
      updateActiveCard(cardEl, q);
    }
  }
}

// ============================================================================
// Targeted Single-Card Updates
// ============================================================================

/** Replace a single card in the DOM by question data */
function replaceOneCard(q: PairwiseQuestion): void {
  const cardEl = document.getElementById(`q-card-${q.id}`);
  if (!cardEl) return;

  const temp = document.createElement('div');
  temp.innerHTML = renderPairwiseCard(q);
  const newCard = temp.firstElementChild;
  if (!newCard) return;

  cardEl.replaceWith(newCard);

  // Update answerMap
  if (q.my_answer) {
    answerMap.set(q.id, {
      answerId: q.my_answer.answer_id,
      version: {
        answer_text: q.my_answer.answer_text,
        answer_number: q.my_answer.answer_number,
        answer_date: q.my_answer.answer_date,
        selected_choice_ids: q.my_answer.selected_choice_ids,
        other_text: q.my_answer.other_text,
      },
      comment_text: q.my_answer.comment_text,
    });
  }

  attachCardHandlers(q);
}

/** Recalculate and update the progress bar from currentPairwiseData */
function recalcProgress(): void {
  if (!currentPairwiseData) return;
  const questions = currentPairwiseData.questions as PairwiseQuestion[];
  const total = questions.length;
  const answeredCount = questions.filter(q => q.my_answer).length;
  const theirAnsweredCount = questions.filter(q => q.they_answered || q.their_answer).length;
  const scoredQuestions = questions.filter(q => q.my_answer && q.their_answer && q.ai_comparison);
  const totalMatchScore = scoredQuestions.reduce((sum, q) => sum + matchScore(q), 0);

  const diff = answeredCount - theirAnsweredCount;
  const diffStr = diff > 0 ? `+${diff}` : `${diff}`;
  let statsText = `You: ${answeredCount}/${total} (${diffStr}) · ${escapeBasic(currentOtherUserName)}: ${theirAnsweredCount}/${total}`;
  if (scoredQuestions.length > 0) {
    const pct = Math.round((totalMatchScore / scoredQuestions.length) * 100);
    statsText += ` · ${pct}% match`;
  }

  const progressEl = document.getElementById('pw-progress');
  if (progressEl) progressEl.innerHTML = statsText;
}

/** Re-fetch pairwise data and update only the specified card(s) + progress */
function refetchAndUpdateCards(questionIds: string[]): void {
  if (!currentSetId) return;
  qaApi.getPairwiseAnswers(currentSetId, currentOtherUserId)
    .then(res => {
      const questions = [...res.data.questions] as PairwiseQuestion[];
      sortPairwiseQuestions(questions);
      currentPairwiseData = { set: res.data.set, questions };

      for (const qid of questionIds) {
        const q = questions.find(pq => pq.id === qid);
        if (q) replaceOneCard(q);
      }
      recalcProgress();
    })
    .catch(() => {});
}

/**
 * Optimistic local update after saving an answer.
 * Immediately updates the saved card without a network round-trip.
 * If the other user had already answered, does a background re-fetch
 * for that single card to resolve reciprocity.
 */
function applyOptimisticSave(
  q: QaQuestionItem,
  answerId: string,
  answerData: Record<string, any>,
): void {
  if (!currentPairwiseData) return;

  const questions = currentPairwiseData.questions as PairwiseQuestion[];
  const origIdx = questions.findIndex(pq => pq.id === q.id);
  if (origIdx === -1) return;

  const origQ = questions[origIdx] as PairwiseQuestion;
  const hadTheyAnswered = origQ.they_answered;

  // Build optimistic question with the new answer
  const needsModeration = q.answer_type === 'free_text';
  const updatedQ: PairwiseQuestion = {
    ...origQ,
    my_answer: {
      answer_id: answerId,
      answer_text: answerData.answer_text ?? null,
      answer_number: answerData.answer_number ?? null,
      answer_date: answerData.answer_date ?? null,
      selected_choice_ids: answerData.selected_choice_ids ?? null,
      other_text: answerData.other_text ?? null,
      comment_text: answerData.comment_text ?? origQ.my_answer?.comment_text ?? null,
      mod_score: needsModeration ? 'pending' : null,
    },
    their_answer: needsModeration ? null : origQ.their_answer,
    ai_comparison: null,
  };

  // Update cached data
  questions[origIdx] = updatedQ;

  // Replace just this card
  replaceOneCard(updatedQ);
  recalcProgress();

  // If they had answered AND this is a new answer (not an edit),
  // background re-fetch to resolve reciprocity reveal.
  // Skip for edits: reciprocity is already met, and the redundant
  // refetch can cause a race that briefly overwrites their_answer data.
  if (hadTheyAnswered && !origQ.my_answer) {
    refetchAndUpdateCards([q.id]);
  }
}

/**
 * Optimistic local update after deleting an answer.
 * Resets the card to unanswered state.
 */
function applyOptimisticDelete(q: QaQuestionItem): void {
  if (!currentPairwiseData) return;

  const questions = currentPairwiseData.questions as PairwiseQuestion[];
  const origIdx = questions.findIndex(pq => pq.id === q.id);
  if (origIdx === -1) return;

  const origQ = questions[origIdx] as PairwiseQuestion;
  const updatedQ: PairwiseQuestion = {
    ...origQ,
    my_answer: null,
    their_answer: null,
    ai_comparison: null,
  };

  questions[origIdx] = updatedQ;
  replaceOneCard(updatedQ);
  recalcProgress();
}

// ============================================================================
// Edit Button + Save Button Helpers
// ============================================================================

/** Attach Edit/Cancel toggle for cards where user has already answered */
function attachEditButton(q: PairwiseQuestion): void {
  const btn = document.getElementById(`pw-edit-btn-${q.id}`);
  const myAnswer = document.getElementById(`pw-my-${q.id}`);
  if (!btn || !myAnswer) return;

  let editing = false;

  btn.addEventListener('click', () => {
    editing = !editing;
    const editArea = myAnswer.querySelector('.pw-edit-area') as HTMLElement | null;

    if (editing) {
      myAnswer.classList.add('editing');
      btn.textContent = 'Cancel';

      // Populate edit area with controls
      if (editArea) {
        const entry = answerMap.get(q.id);
        const isNonFreeText = q.answer_type !== 'free_text';
        editArea.innerHTML = `
          <div class="qs-card-control" id="q-control-${q.id}">
            ${renderControl(q as QaQuestionItem, entry)}
            ${isNonFreeText ? renderCommentInput(q.id, entry?.comment_text) : ''}
            ${renderSaveButton(q.id)}
          </div>
        `;

        // Attach handlers — all types skip auto-save, use Save button
        attachQuestionHandlers(q as QaQuestionItem, () => {}, {
          skipFreeTextAutoSave: true,
          skipMultiChoiceAutoSave: true,
          skipNumericAutoSave: true,
          skipSingleChoiceAutoSave: true,
          skipDateAutoSave: true,
          skipScaleAutoSave: true,
        });
        attachFreeTextSaveButton(q);
      }
    } else {
      // Cancel: revert to read-only
      myAnswer.classList.remove('editing');
      btn.textContent = 'Edit';
      clearDebounce(`pw-${q.id}`);
      if (editArea) editArea.innerHTML = '';
    }
  });
}

/** Attach Reply toggle + send handler for the other person's revealed answer */
function attachReplyButton(q: PairwiseQuestion): void {
  const btn = document.getElementById(`pw-reply-btn-${q.id}`);
  const replyArea = document.getElementById(`pw-reply-area-${q.id}`);
  if (!btn || !replyArea) return;

  let open = false;

  btn.addEventListener('click', () => {
    open = !open;

    if (open) {
      btn.textContent = 'Cancel';
      replyArea.style.display = '';
      replyArea.innerHTML = `
        <div class="pw-reply-input-row">
          <input type="text" class="input pw-reply-input" placeholder="Reply..." maxlength="500" />
          <button class="btn btn-primary btn-sm">Send</button>
        </div>
      `;
      const input = replyArea.querySelector('.pw-reply-input') as HTMLInputElement;
      const sendBtn = replyArea.querySelector('.btn-primary') as HTMLButtonElement;
      input?.focus();

      const doSend = async () => {
        const text = input?.value.trim();
        if (!text || !currentChatId) return;

        const answerText = getPlainAnswerText(q, q.their_answer!);
        const truncated = answerText.length > 60 ? answerText.slice(0, 57) + '...' : answerText;
        const content = `{{QA_REPLY|${q.order_index}|${truncated}|${currentSetId}|${q.id}}}${text}`;

        sendBtn.disabled = true;
        input.disabled = true;

        try {
          await chatsApi.sendMessage(currentChatId, content);
          showToast(TOAST_SENT);
          open = false;
          btn.textContent = 'Reply';
          replyArea.style.display = 'none';
          replyArea.innerHTML = '';
        } catch (err: any) {
          showApiError(err, API_ERR_SEND_REPLY);
          sendBtn.disabled = false;
          input.disabled = false;
        }
      };

      sendBtn?.addEventListener('click', doSend);
      input?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') doSend();
      });
    } else {
      btn.textContent = 'Reply';
      replyArea.style.display = 'none';
      replyArea.innerHTML = '';
    }
  });
}

/** Attach click handler for Save button on free_text fields (no auto-save) */
function attachFreeTextSaveButton(q: PairwiseQuestion | QaQuestionItem): void {
  const saveBtn = document.getElementById(`qa-save-${q.id}`);
  if (!saveBtn) return;

  saveBtn.addEventListener('click', () => executePairwiseSave(q as QaQuestionItem));
}

/** Immediate (non-debounced) save for pairwise answers */
async function executePairwiseSave(q: QaQuestionItem): Promise<void> {
  const data = collectAnswerData(q);

  // Empty free_text with an existing answer → delete (soft-delete) the answer
  const existing = answerMap.get(q.id);
  if (!data && q.answer_type === 'free_text' && existing) {
    const saveBtn = document.getElementById(`qa-save-${q.id}`) as HTMLButtonElement | null;
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving...'; }
    try {
      await qaApi.deleteAnswer(existing.answerId);
      answerMap.delete(q.id);
      showSaveIndicator(`indicator-q-${q.id}`, true);
      applyOptimisticDelete(q);
    } catch (err: any) {
      showSaveIndicator(`indicator-q-${q.id}`, false, 'Error');
      showApiError(err, API_ERR_DELETE_ANSWER);
      if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
    }
    return;
  }

  if (!data) return;

  // Include comment_text for non-free-text questions
  const comment = collectCommentText(q.id);
  if (comment !== undefined) data.comment_text = comment;

  const saveBtn = document.getElementById(`qa-save-${q.id}`) as HTMLButtonElement | null;
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';
  }

  try {
    let answerId: string;
    let commentRejected = false;
    if (existing) {
      const res = await qaApi.editAnswer(existing.answerId, data);
      answerId = res.data.answer.id;
      commentRejected = !!res.data.comment_rejected;
      answerMap.set(q.id, { answerId, version: res.data.version, comment_text: commentRejected ? null : (comment || null) });
    } else {
      const res = await qaApi.submitAnswer({ question_id: q.id, ...data });
      answerId = res.data.answer.id;
      commentRejected = !!res.data.comment_rejected;
      answerMap.set(q.id, { answerId, version: res.data.version, comment_text: commentRejected ? null : (comment || null) });
    }
    if (commentRejected) showToast(TOAST_COMMENT_REJECTED, 'error');
    showSaveIndicator(`indicator-q-${q.id}`, true);

    // Targeted update: rebuild just this card (no full re-fetch)
    applyOptimisticSave(q, answerId, data);
  } catch (err: any) {
    showSaveIndicator(`indicator-q-${q.id}`, false, 'Error');
    showApiError(err, API_ERR_SAVE_ANSWER);
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  }
}

